import styled from "styled-components";

const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Section = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(72px, 9vw, 120px) 0;
  background: ${(p) => p.$bg || "#ffffff"};
  border-top: ${(p) => (p.$divider ? "1px solid #ede9fe" : "none")};
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #7c3aed;
  margin: 0 0 16px;
`;

const Heading = styled.h2`
  font-size: clamp(32px, 4.2vw, 52px);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const Lead = styled.p`
  font-size: clamp(16px, 1.4vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto;
  max-width: 680px;
`;

const SectionHeader = styled.div`
  margin-bottom: 64px;
  text-align: center;
`;

/* ---- Hero ---- */
/* Pull up under the global Section margin in main.js so the gradient
   reaches all the way to the fixed header. Add the same amount back
   as padding-top so visual spacing stays the same. */
const Hero = styled.section`
  display: block;
  min-height: auto;
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(80px, 10vw, 130px);
  text-align: center;
  background:
    radial-gradient(ellipse 80% 60% at 50% 0%, #f5f3ff 0%, transparent 70%),
    #ffffff;
  position: relative;
  overflow: hidden;
`;

const HeroVideo = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  /* scale up to hide blurred edges */
  transform: scale(1.1);
  filter: blur(9px) saturate(1.15);
  opacity: 0.75;
  pointer-events: none;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(245, 243, 255, 0.25) 0%,
    rgba(255, 255, 255, 0.6) 55%,
    rgba(255, 255, 255, 0.88) 100%
  );
  pointer-events: none;
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;
`;

const Wordmark = styled.img`
  width: 100%;
  max-width: 540px;
  height: auto;
  margin: 0 auto 40px;
  display: block;
`;

const HeroLead = styled.p`
  font-size: clamp(17px, 1.6vw, 21px);
  line-height: 1.55;
  color: #475569;
  margin: 0 auto 40px;
  max-width: 600px;
`;

const CTARow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 26px;
  background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
  color: #ffffff;
  font-weight: 500;
  font-size: 15px;
  border-radius: 999px;
  text-decoration: none;
  box-shadow: 0 10px 24px -8px rgba(124, 58, 237, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 30px -10px rgba(124, 58, 237, 0.55);
    color: #ffffff;
  }
  &:active {
    transform: translateY(1px);
  }
`;

const GhostButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 14px 22px;
  background: transparent;
  color: #6d28d9;
  font-weight: 500;
  font-size: 15px;
  border-radius: 999px;
  text-decoration: none;
  border: 1px solid #ddd6fe;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #faf5ff;
    border-color: #c4b5fd;
    color: #6d28d9;
  }
`;

/* ---- Features (4 large photo cards, 2x2) ---- */
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FeatureCard = styled.article`
  background: #ffffff;
  border: 1px solid #ede9fe;
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: #c4b5fd;
    box-shadow: 0 24px 40px -20px rgba(124, 58, 237, 0.2);
  }

  .photo {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    display: block;
  }
  .body {
    padding: 28px 28px 32px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .num {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #7c3aed;
    margin: 0;
  }
  h3 {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.015em;
    line-height: 1.25;
  }
  p {
    font-size: 15px;
    line-height: 1.65;
    color: #64748b;
    margin: 6px 0 0;
  }
`;

/* ---- Places (4 illustrated icon cards) ---- */
const PlaceBand = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(72px, 9vw, 120px) 0;
  background: linear-gradient(180deg, #faf5ff 0%, #ffffff 100%);
  border-top: 1px solid #ede9fe;
  border-bottom: 1px solid #ede9fe;
`;

const PlacesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Place = styled.div`
  background: #ffffff;
  border: 1px solid #ede9fe;
  border-radius: 18px;
  padding: 28px 22px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  transition: border-color 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: #c4b5fd;
    transform: translateY(-2px);
    box-shadow: 0 16px 30px -18px rgba(124, 58, 237, 0.28);
  }

  .icon-wrap {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #7c3aed;
  }
  .icon-wrap svg {
    width: 30px;
    height: 30px;
  }
  .label {
    font-size: 15px;
    font-weight: 600;
    color: #0f172a;
    letter-spacing: -0.005em;
    line-height: 1.35;
  }
`;

/* ---- Closing section linking to the BHP site ---- */
const Bridge = styled.section`
  display: block;
  min-height: auto;
  padding: clamp(96px, 12vw, 140px) 24px;
  text-align: center;
  background: #ffffff;
`;

const BridgeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6d28d9;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 20px;
`;

const features = [
  {
    num: "01",
    image: "/images/playground7.png",
    title: "Expert collaboration",
    desc: "Designed with neurologists, orthopedic specialists, and exercise rehabilitation experts — combining motion and voice recognition with proven cognitive activities.",
  },
  {
    num: "02",
    image: "/images/playground8.png",
    title: "Integrated approach",
    desc: "By pairing cognitive activity with physical movement at the same time, the program supports brain activation through dual-task engagement.",
  },
  {
    num: "03",
    image: "/images/playground9.png",
    title: "Body and brain stimulation",
    desc: "Encourages balance and strength by activating muscles and neural pathways that are not typically used in everyday routines.",
  },
  {
    num: "04",
    image: "/images/playground10.png",
    title: "Senior-friendly design",
    desc: "Built around accessibility — simple gestures, clear visuals, and approachable interactions tailored for older adults.",
  },
];

/* Lucide-style thin-stroke icons — inline for crisp rendering
   and easy color control */
const IconBrain = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 3.5a2.5 2.5 0 0 0-2.5 2.5v.25a2.5 2.5 0 0 0-1.75 4.28A2.5 2.5 0 0 0 6.5 15a2.5 2.5 0 0 0 3 3v.5A2.5 2.5 0 0 0 12 21V4.5A2.5 2.5 0 0 0 9.5 3.5Z"/>
    <path d="M14.5 3.5A2.5 2.5 0 0 1 17 6v.25a2.5 2.5 0 0 1 1.75 4.28A2.5 2.5 0 0 1 17.5 15a2.5 2.5 0 0 1-3 3v.5A2.5 2.5 0 0 1 12 21"/>
  </svg>
);

const IconHeartPulse = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0L12 5.35l-.77-.77a5.41 5.41 0 0 0-7.65 7.65l1.11 1.11h4.44l1.5-3 2 5 1.5-3h3.44l2.85-2.85a5.4 5.4 0 0 0 0-7.9Z"/>
  </svg>
);

const IconBuilding = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 21h18"/>
    <path d="M5 21V7l7-4 7 4v14"/>
    <path d="M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01"/>
  </svg>
);

const IconSunSmile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4.5"/>
    <path d="M10 11.5h.01M14 11.5h.01"/>
    <path d="M9.75 13.5a2.5 2.5 0 0 0 4.5 0"/>
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
  </svg>
);

const places = [
  { icon: <IconBrain />, label: "Cognitive Health Center" },
  { icon: <IconHeartPulse />, label: "Senior Health Center" },
  { icon: <IconBuilding />, label: "Silver Town" },
  { icon: <IconSunSmile />, label: "Day Care Center" },
];

export function BPlayground() {
  return (
    <Page>
      <Hero>
        <HeroVideo
          src="/videos/hero-bg.mp4"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <HeroOverlay aria-hidden="true" />
        <HeroInner>
          <Wordmark src="/images/bhp_wordmark.png" alt="Brain Health Playground" />
          <HeroLead>
            A dual-tasking research program that uses AI motion and voice
            recognition to combine two or more cognitive activities at the same
            time — designed with cognitive scientists for older adults.
          </HeroLead>
          <CTARow>
            <PrimaryButton href="#features">
              Explore the program
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 5v14M5 12l7 7 7-7" />
              </svg>
            </PrimaryButton>
            <GhostButton
              href="https://bhp.youngandx.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit BHP site
            </GhostButton>
          </CTARow>
        </HeroInner>
      </Hero>

      <Section id="features" $divider>
        <Container>
          <SectionHeader>
            <Eyebrow>Features &amp; Benefits</Eyebrow>
            <Heading>A research-driven approach to cognitive wellness.</Heading>
            <Lead>
              Built through a digital training program that supports cognitive
              wellness and continuous monitoring, with a customized AI algorithm
              behind every session.
            </Lead>
          </SectionHeader>

          <FeatureGrid>
            {features.map((f) => (
              <FeatureCard key={f.num}>
                <img className="photo" src={f.image} alt={f.title} />
                <div className="body">
                  <p className="num">{f.num}</p>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </FeatureCard>
            ))}
          </FeatureGrid>
        </Container>
      </Section>

      <PlaceBand>
        <Container>
          <SectionHeader>
            <Eyebrow>Place of use</Eyebrow>
            <Heading>Built for the places older adults already go.</Heading>
            <Lead>
              The program fits naturally into existing care environments —
              cognitive health centers, senior health centers, silver towns, and
              day care centers.
            </Lead>
          </SectionHeader>

          <PlacesGrid>
            {places.map((p) => (
              <Place key={p.label}>
                <div className="icon-wrap">{p.icon}</div>
                <div className="label">{p.label}</div>
              </Place>
            ))}
          </PlacesGrid>
        </Container>
      </PlaceBand>

      <Bridge>
        <Container>
          <BridgeBadge>Learn more</BridgeBadge>
          <Heading style={{ marginBottom: 16 }}>
            Explore Brain Health Playground.
          </Heading>
          <Lead style={{ margin: "0 auto 32px" }}>
            Visit the full Brain Health Playground site for program details,
            research, and resources.
          </Lead>
          <CTARow>
            <PrimaryButton
              href="https://bhp.youngandx.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit bhp.youngandx.com
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M9 7h8v8" />
              </svg>
            </PrimaryButton>
          </CTARow>
        </Container>
      </Bridge>
    </Page>
  );
}
